import React from 'react';
import { useNavigate } from 'react-router-dom';

import { ArrowForwardIos } from '@mui/icons-material';
import { Grid, Skeleton, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { NoDataBox } from 'components/GlobalStyles/styles';
import GridItem from 'components/GridItem';
import { TeamBanner, TeamsBox } from 'pages/Teams/styles';
import { Team } from 'services/busmanApi/types';
import { getAllTeamMembers } from 'utils/teams';

interface TeamsViewProps {
  isFetching: boolean;
  filteredTeams: Array<Team>;
  searchInput: string;
}

const skeletonCount = 8;

const TeamsView = ({ isFetching, filteredTeams, searchInput }: TeamsViewProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleNavigate = (id: string) => {
    navigate(`/teams/${id}`);
  };

  return (
    <>
      {!isFetching && filteredTeams?.length === 0 ? (
        <NoDataBox>
          <Typography sx={{ padding: theme.spacing(2) }} variant="h6">
            {searchInput ? 'No teams found !' : 'No teams...'}
          </Typography>
        </NoDataBox>
      ) : (
        <TeamsBox>
          <Grid container display="flex" flexDirection="row" justifyContent="flex-start" sx={{ mb: theme.spacing(2) }}>
            <GridItem title={'Teams'} seenOrIsHeading={true} breakpoint={4} />
            {!isMobileScreen && <GridItem title={'Manager(s)'} seenOrIsHeading={true} breakpoint={3} />}
            <GridItem title={'Pods'} seenOrIsHeading={true} breakpoint={2} />
            <GridItem title={'Members'} seenOrIsHeading={true} breakpoint={2.5} />
          </Grid>
          {isFetching ? (
            <>
              {Array.from({ length: skeletonCount }).map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={56} sx={{ mb: theme.spacing(1) }} />
              ))}
            </>
          ) : (
            filteredTeams?.map((team) => {
              const podsTotal = team.members.filter((member) => member.is_team);
              return (
                <TeamBanner
                  container
                  justifyContent="flex-start"
                  key={team.team_id}
                  onClick={() => handleNavigate(team.team_id)}
                  sx={{ border: `1px solid ${theme.palette.background.default}`, height: '56px' }}>
                  <GridItem title={team.team_name} seenOrIsHeading={false} breakpoint={4} />
                  {!isMobileScreen && (
                    <GridItem title={team.managers[0].full_name} seenOrIsHeading={false} breakpoint={3} />
                  )}
                  <GridItem title={podsTotal.length.toString()} seenOrIsHeading={false} breakpoint={2} />
                  <GridItem title={getAllTeamMembers(team).size.toString()} seenOrIsHeading={false} breakpoint={2} />
                  <ArrowForwardIos
                    sx={{
                      color: theme.palette.text.disabled,
                      fontSize: '0.8rem',
                      marginLeft: 'auto',
                      marginRight: theme.spacing(2),
                    }}
                  />
                </TeamBanner>
              );
            })
          )}
        </TeamsBox>
      )}
    </>
  );
};

export default TeamsView;
